import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  confirm,
  Input,
  Label,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from "@devhop/ui";
import {
  BarChart3Icon,
  PencilIcon,
  PlusIcon,
  SettingsIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  dateTimeLocalValueToStorageTimestamp,
  toDateTimeLocalValue,
} from "@/shared/lib/datetime-local-form";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { DateQuarterTimeField } from "@/shared/ui/DateQuarterTimeField";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { AudienceVoteContestantDTO } from "../api/client";
import { audienceVoteApi } from "../api/client";
import {
  useAudienceVoteEventQuery,
  useAudienceVoteEventsQuery,
  useCreateAudienceVoteContestantMutation,
  useDeleteAudienceVoteContestantMutation,
  usePatchAudienceVoteEventMutation,
  useUpdateAudienceVoteContestantMutation,
  useUpdateAudienceVoteEventMutation,
} from "../api/queries";
import { AudienceVoteContestantArtworkCell } from "../ui/AudienceVoteContestantArtworkCell";
import { AudienceVoteContestantDialog } from "../ui/AudienceVoteContestantDialog";
import { AudienceVoteResultsTab } from "../ui/AudienceVoteResultsTab";

type AdminTab = "settings" | "contestants" | "results";

const STATUS_LABELS = {
  draft: "Draft",
  open: "Open",
  closed: "Closed",
} as const;

const PHASE_LABELS = {
  draft: "Draft",
  scheduled: "Scheduled",
  open: "Open",
  closed: "Closed",
} as const;

const CONTESTANT_STATUS_LABELS = {
  draft: "Draft",
  live: "Live",
  withdrawn: "Withdrawn",
} as const;

export function AudienceVotePage() {
  const canWrite = useActionPermission(["audience-vote:write"]);
  const canExport = useActionPermission(["audience-vote:export"]);

  const { data: eventsData, isLoading: eventsLoading } =
    useAudienceVoteEventsQuery();
  const events = eventsData?.items ?? [];

  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<AdminTab>("settings");

  useEffect(() => {
    if (selectedEventId) return;
    if (events.length === 0) return;
    const first = events[0];
    if (first) setSelectedEventId(first.id);
  }, [events, selectedEventId]);

  const { data: detail, isLoading: detailLoading } = useAudienceVoteEventQuery(
    selectedEventId || undefined,
  );

  const updateEventMutation = useUpdateAudienceVoteEventMutation();
  const patchEventMutation = usePatchAudienceVoteEventMutation();
  const createContestantMutation = useCreateAudienceVoteContestantMutation();
  const updateContestantMutation = useUpdateAudienceVoteContestantMutation();
  const deleteContestantMutation = useDeleteAudienceVoteContestantMutation();

  const event = detail?.event;
  const phase = detail?.phase ?? "draft";

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [opensLocal, setOpensLocal] = useState("");
  const [closesLocal, setClosesLocal] = useState("");
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!event) return;
    setTitle(event.title);
    setSlug(event.slug ?? "");
    setOpensLocal(toDateTimeLocalValue(event.opensAt));
    setClosesLocal(toDateTimeLocalValue(event.closesAt));
    setShowResults(event.showResults);
  }, [event]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContestant, setEditingContestant] =
    useState<AudienceVoteContestantDTO | null>(null);

  const nextSortOrder = useMemo(() => {
    const list = detail?.contestants ?? [];
    if (list.length === 0) return 1;
    return Math.max(...list.map((c) => c.sortOrder)) + 1;
  }, [detail?.contestants]);

  const saveSettings = async () => {
    if (!event?.id) return;
    const opensAt = dateTimeLocalValueToStorageTimestamp(opensLocal);
    const closesAt = dateTimeLocalValueToStorageTimestamp(closesLocal);
    if (opensLocal.trim() && !opensAt) {
      toast.error("ເວລາເປີດບໍ່ຖືກຕ້ອງ");
      return;
    }
    if (closesLocal.trim() && !closesAt) {
      toast.error("ເວລາປິດບໍ່ຖືກຕ້ອງ");
      return;
    }
    try {
      await updateEventMutation.mutateAsync({
        eventId: event.id,
        body: {
          title: title.trim(),
          slug: slug.trim() || null,
          opensAt,
          closesAt,
          showResults,
        },
      });
      toast.success("ບັນທຶກການຕັ້ງຄ່າແລ້ວ");
    } catch {
      toast.error("ບັນທຶກບໍ່ສຳເລັດ");
    }
  };

  const handleStatusChange = async (status: "draft" | "open" | "closed") => {
    if (!event?.id) return;
    try {
      await updateEventMutation.mutateAsync({
        eventId: event.id,
        body: { status },
      });
      toast.success("ອັບເດດສະຖານະແລ້ວ");
    } catch {
      toast.error("ອັບເດດສະຖານະບໍ່ສຳເລັດ");
    }
  };

  const handleForceClose = async () => {
    if (!event?.id) return;
    const ok = await confirm({
      title: "ປິດໂຫວດທັນທີ",
      description: "ຕັ້ງ status ເປັນ closed — ຜູ້ໂຫວດຈະຖອນຄະແນນບໍ່ໄດ້",
    });
    if (!ok) return;
    try {
      await patchEventMutation.mutateAsync({
        eventId: event.id,
        body: { status: "closed" },
      });
      toast.success("ປິດ event ແລ້ວ");
    } catch {
      toast.error("ປິດ event ບໍ່ສຳເລັດ");
    }
  };

  const handleToggleShowResults = async (next: boolean) => {
    if (!event?.id) return;
    setShowResults(next);
    try {
      await patchEventMutation.mutateAsync({
        eventId: event.id,
        body: { showResults: next },
      });
      toast.success("ອັພເດດການເຜີຍແຜ່ຜົນແລ້ວ");
    } catch {
      setShowResults(event.showResults);
      toast.error("ອັພເດດບໍ່ສຳເລັດ");
    }
  };

  const openCreateDialog = () => {
    setEditingContestant(null);
    setDialogOpen(true);
  };

  const openEditDialog = (c: AudienceVoteContestantDTO) => {
    setEditingContestant(c);
    setDialogOpen(true);
  };

  const handleDeleteContestant = async (c: AudienceVoteContestantDTO) => {
    if (!event?.id) return;
    const ok = await confirm({
      title: "ລຶບນັກແຂ່ງ",
      description: `ຢືນຢັນລຶບ「${c.displayName}」?`,
    });
    if (!ok) return;
    try {
      await deleteContestantMutation.mutateAsync({
        eventId: event.id,
        contestantId: c.id,
      });
      toast.success("ລຶບແລ້ວ");
    } catch {
      toast.error("ລຶບບໍ່ສຳເລັດ");
    }
  };

  const handleExportCsv = async () => {
    if (!event?.id) return;
    try {
      await audienceVoteApi.downloadTallyCsv(event.id);
      toast.success("ດາວໂຫຼດ CSV ແລ້ວ");
    } catch {
      toast.error("ດາວໂຫຼດ CSV ບໍ່ສຳເລັດ");
    }
  };

  const loading = eventsLoading || (selectedEventId && detailLoading);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Events", href: "/app/audience-vote" },
            { label: "Audience Vote" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-semibold text-2xl">Audience Vote</h1>
            <p className="text-muted-foreground text-sm">
              ຕັ້ງຄ່າເວລາ · ນັກແຂ່ງ · ຜົນໂຫວດ (LIHF)
            </p>
          </div>
          {events.length > 1 ? (
            <div className="min-w-48">
              <SimpleSelect
                value={selectedEventId}
                onValueChange={setSelectedEventId}
                options={events.map((e) => ({
                  value: e.id,
                  label: e.title,
                }))}
              />
            </div>
          ) : null}
        </div>

        {loading ? (
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        ) : !event || !detail ? (
          <Card>
            <CardHeader>
              <CardTitle>ຍັງບໍ່ມີ Event</CardTitle>
              <CardDescription>
                ສ້າງ event ໃນ migration/seed ຫຼື POST /api/audience-vote/events
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline">{event.id}</Badge>
              {event.slug ? (
                <Badge variant="secondary">slug: {event.slug}</Badge>
              ) : null}
              <Badge
                variant={
                  phase === "open"
                    ? "default"
                    : phase === "scheduled"
                      ? "secondary"
                      : "outline"
                }
              >
                phase: {PHASE_LABELS[phase]}
              </Badge>
            </div>

            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as AdminTab)}
              className="w-full"
            >
              <div className="overflow-x-auto">
                <TabsList className="h-auto w-full min-w-max border-b bg-transparent p-0">
                  <TabsTrigger
                    value="settings"
                    className="gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                  >
                    <SettingsIcon className="size-4" />
                    Settings
                  </TabsTrigger>
                  <TabsTrigger
                    value="contestants"
                    className="gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                  >
                    <UsersIcon className="size-4" />
                    Contestants
                  </TabsTrigger>
                  <TabsTrigger
                    value="results"
                    className="gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
                  >
                    <BarChart3Icon className="size-4" />
                    Results
                  </TabsTrigger>
                </TabsList>
              </div>

              {activeTab === "settings" ? (
                <div className="mt-6 grid gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>ຕັ້ງຄ່າ Event</CardTitle>
                      <CardDescription>
                        ເວລາໃນ browser ເປັນ local → ບັນທຶກເປັນ UTC (ISO)
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="av-title">ຊື່ event</Label>
                        <Input
                          id="av-title"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          disabled={!canWrite}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="av-slug">Slug (LIHF URL)</Label>
                        <Input
                          id="av-slug"
                          value={slug}
                          onChange={(e) => setSlug(e.target.value)}
                          disabled={!canWrite}
                          placeholder="graffiti-2026-d1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ສະຖານະ admin</Label>
                        <SimpleSelect
                          value={event.status}
                          onValueChange={(v) =>
                            handleStatusChange(v as "draft" | "open" | "closed")
                          }
                          disabled={!canWrite || updateEventMutation.isPending}
                          options={[
                            { value: "draft", label: STATUS_LABELS.draft },
                            { value: "open", label: STATUS_LABELS.open },
                            {
                              value: "closed",
                              label: STATUS_LABELS.closed,
                            },
                          ]}
                        />
                      </div>
                      <DateQuarterTimeField
                        label="ເປີດໂຫວດ (local)"
                        value={opensLocal}
                        onChange={(v) => setOpensLocal(v ?? "")}
                        disabled={!canWrite}
                        allowEmpty
                      />
                      <DateQuarterTimeField
                        label="ປິດໂຫວດ (local)"
                        value={closesLocal}
                        onChange={(v) => setClosesLocal(v ?? "")}
                        disabled={!canWrite}
                        allowEmpty
                      />
                      <div className="flex items-center justify-between gap-3 sm:col-span-2">
                        <div>
                          <Label htmlFor="av-show-results">
                            ເຜີຍແຜ່ຜົນໃນ LIHF
                          </Label>
                          <p className="text-muted-foreground text-xs">
                            ຫຼັງ close + ເປີດ showResults
                          </p>
                        </div>
                        <Switch
                          id="av-show-results"
                          checked={showResults}
                          onCheckedChange={handleToggleShowResults}
                          disabled={!canWrite || patchEventMutation.isPending}
                        />
                      </div>
                      {canWrite ? (
                        <div className="flex flex-wrap gap-2 sm:col-span-2">
                          <Button
                            onClick={saveSettings}
                            disabled={updateEventMutation.isPending}
                          >
                            ບັນທຶກການຕັ້ງຄ່າ
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            onClick={handleForceClose}
                            disabled={
                              patchEventMutation.isPending ||
                              event.status === "closed"
                            }
                          >
                            Force close
                          </Button>
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                </div>
              ) : null}

              {activeTab === "contestants" ? (
                <div className="mt-6 space-y-4">
                  <div className="flex justify-end">
                    {canWrite ? (
                      <Button onClick={openCreateDialog}>
                        <PlusIcon className="mr-2 size-4" />
                        ເພີ່ມນັກແຂ່ງ
                      </Button>
                    ) : null}
                  </div>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-left">
                        <tr>
                          <th className="p-2">ລຳດັບ</th>
                          <th className="p-2">ຊື່</th>
                          <th className="p-2">ຜົນງານ</th>
                          <th className="p-2">Panel</th>
                          <th className="p-2">ສະຖານະ</th>
                          <th className="p-2 text-right">ຈັດການ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(detail.contestants ?? [])]
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map((c) => (
                            <tr key={c.id} className="border-t">
                              <td className="p-2 tabular-nums">
                                {c.sortOrder}
                              </td>
                              <td className="p-2">
                                <div className="font-medium">
                                  {c.displayName}
                                </div>
                                {c.subtitle ? (
                                  <div className="text-muted-foreground text-xs">
                                    {c.subtitle}
                                  </div>
                                ) : null}
                              </td>
                              <td className="p-2">
                                <AudienceVoteContestantArtworkCell
                                  eventId={event.id}
                                  contestant={c}
                                />
                              </td>
                              <td className="p-2">{c.panelLabel ?? "—"}</td>
                              <td className="p-2">
                                <Badge variant="outline">
                                  {CONTESTANT_STATUS_LABELS[c.status]}
                                </Badge>
                              </td>
                              <td className="p-2 text-right">
                                {canWrite ? (
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => openEditDialog(c)}
                                    >
                                      <PencilIcon className="size-4" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => handleDeleteContestant(c)}
                                      disabled={
                                        deleteContestantMutation.isPending
                                      }
                                    >
                                      <Trash2Icon className="size-4" />
                                    </Button>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {detail.contestants.length === 0 ? (
                      <p className="p-4 text-center text-muted-foreground text-sm">
                        ຍັງບໍ່ມີນັກແຂ່ງ
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {activeTab === "results" ? (
                <AudienceVoteResultsTab
                  eventId={event.id}
                  phase={phase}
                  showResults={event.showResults}
                  uniqueVoters={detail.stats.uniqueVoters}
                  totalVotes={detail.stats.totalVotes}
                  contestants={detail.contestants}
                  canExport={canExport}
                  onExportCsv={handleExportCsv}
                />
              ) : null}
            </Tabs>

            <AudienceVoteContestantDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              eventId={event.id}
              canWrite={canWrite}
              contestant={editingContestant}
              nextSortOrder={nextSortOrder}
              isPending={
                createContestantMutation.isPending ||
                updateContestantMutation.isPending
              }
              onCreate={async (body) => {
                if (!event.id) return;
                try {
                  await createContestantMutation.mutateAsync({
                    eventId: event.id,
                    body,
                  });
                  toast.success("ເພີ່ມນັກແຂ່ງແລ້ວ");
                  setDialogOpen(false);
                } catch {
                  toast.error("ເພີ່ມບໍ່ສຳເລັດ");
                }
              }}
              onUpdate={async (contestantId, body) => {
                if (!event.id) return;
                try {
                  await updateContestantMutation.mutateAsync({
                    eventId: event.id,
                    contestantId,
                    body,
                  });
                  toast.success("ບັນທຶກແລ້ວ");
                  setDialogOpen(false);
                } catch {
                  toast.error("ບັນທຶກບໍ່ສຳເລັດ");
                }
              }}
            />
          </>
        )}
      </Main>
    </>
  );
}
